<template>
  <div class="otj-progress-chart">
    <template v-if="ready">
      <k-chart-mixed-lines
        :series="mixedLineSeries"
        :xaxisData="dateLabels"
        xaxisTitle="Month"
        yaxisTitle="Time Spent (hours)"
        :colours="mixedLineColours"
        :dashArray="mixedLineDashArray"
        :strokeCurve="mixedLineStrokeCurve"
        :accessibilityLabel="chartAccessibilityLabel"
        :accessibilityDescription="chartAccessibilityDescription"
        :customTooltip="formatCustomTooltip"
        :xaxisAnnotations="graduationXaxisAnnotations"
      />
    </template>
    <k-loader v-else class="k-loader" />
  </div>
</template>

<style>
.k-loader {
  display: flex;
  justify-content: center;
}

.custom-tooltip-content {
  background-color: var(--kate-background-body);
  font-size: 12px;
  padding: 8px 12px;
  border-radius: 4px;
}

.tooltip-block-title {
  background: var(--kate-background);
  font-size: 12px;
  font-weight: bold;
  padding: 8px 12px;
  margin: -8px -12px 4px;
  border-radius: 4px 4px 0 0;
}

.tooltip-section {
  margin-bottom: 8px;
}

.tooltip-section.last {
  margin-bottom: 20px;
}

.tooltip-section-title {
  font-size: 12px;
  font-weight: bold;
  margin-bottom: 4px;
}

.tooltip-series-item {
  display: flex;
  align-items: center;
  margin: 2px 0;
  font-size: 12px;
}

.tooltip-marker {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 6px;
  flex-shrink: 0;
  border: 1px solid var(--kate-type-dark);
}

.tooltip-series-name {
  margin-right: 6px;
}

.otj-progress-chart {
  --otj-graduation-line-color: var(--kate-type-alt-dark);
}

[data-theme="light"] .otj-progress-chart {
  --otj-graduation-line-color: var(--kate-panel-disabled);
}

.otj-progress-chart line.otj-graduation-chart-line {
  stroke: var(--otj-graduation-line-color) !important;
}

.otj-progress-chart .otj-graduation-chart-label.apexcharts-annotation-rect {
  stroke: var(--otj-graduation-line-color) !important;
  stroke-width: 1px !important;
}

[data-theme="light"] .otj-progress-chart .otj-graduation-chart-label.apexcharts-annotation-rect {
  fill: var(--kate-panel) !important;
}

[data-theme="dark"] .otj-progress-chart .otj-graduation-chart-label.apexcharts-annotation-rect,
[data-theme="legacy"] .otj-progress-chart .otj-graduation-chart-label.apexcharts-annotation-rect {
  fill: var(--kate-type-dark) !important;
}
</style>

<script>
import { formatInTimeZone } from 'date-fns-tz';
import TimeMixin from '@mixins/time-mixins';
import useGlobalStore from '@stores/global';
import KChartMixedLines from '@base-components/charts/k-chart-mixed-lines.vue';
import KLoader from '@base-components/k-loader.vue';

const CHART_VISIBLE_STATUSES = ['approved', 'pending', 'draft']; // Rejected excluded from chart
const COLOUR_MAP = {
  approved: 'var(--kate-line-success)',
  pending: 'var(--kate-line-primary)',
  draft: 'var(--kate-line-secondary)',
};

export default {
  components: { KChartMixedLines, KLoader },
  mixins: [TimeMixin],

  beforeMount() {
    this.getOtjChartData();
  },

  props: {
    userId: {
      type: Number,
    },
    programmeId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      store: useGlobalStore(),
      otjProgress: [],
      ready: false,
    };
  },

  watch: {
    programmeId() {
      this.getOtjChartData();
    },
  },
  computed: {
    isApp() {
      return this.store.isApp;
    },
    recordedMonthlyOtj() {
      return this.otjProgress?.recorded_monthly_otj || [];
    },
    approvedMonthlyOtj() {
      return this.recordedMonthlyOtj.filter(e => e.status === 'approved');
    },
    pendingMonthlyOtj() {
      return this.recordedMonthlyOtj.filter(e => e.status === 'pending');
    },
    draftMonthlyOtj() {
      return this.recordedMonthlyOtj.filter(e => e.status === 'draft');
    },
    expectedMonthlyOtj() {
      return this.otjProgress?.expected_monthly_otj || [];
    },
    dateLabels() {
      return this.expectedMonthlyOtj.map(e => e.pretty_period);
    },
    graduationMonthEntry() {
      const plannedEndDate = this.otjProgress?.ilr_planned_end_date;
      if (!plannedEndDate) return null;
      const period = formatInTimeZone(this.getDate(plannedEndDate), 'UTC', 'yyyy-MM');
      return this.expectedMonthlyOtj.find(entry => entry.period === period) || null;
    },
    graduationMonthIndex() {
      if (!this.graduationMonthEntry) return -1;
      return this.expectedMonthlyOtj.findIndex(
        entry => entry.period === this.graduationMonthEntry.period,
      );
    },
    formattedGraduationDate() {
      const plannedEndDate = this.otjProgress?.ilr_planned_end_date;
      if (!plannedEndDate) return '';
      return this.parseTimestamp(plannedEndDate, undefined, 'do MMM');
    },
    graduationXaxisAnnotations() {
      if (!this.graduationMonthEntry) return [];

      const isLastMonth = this.graduationMonthIndex === this.expectedMonthlyOtj.length - 1;

      return [{
        id: 'otj-graduation-chart-line',
        x: this.graduationMonthEntry.pretty_period,
        strokeDashArray: 0,
        borderColor: 'var(--otj-graduation-line-color)',
        borderWidth: 2,
        label: {
          text: `Planned end date: ${this.formattedGraduationDate}`,
          borderColor: 'var(--otj-graduation-line-color)',
          borderWidth: 1,
          style: {
            cssClass: 'otj-graduation-chart-label',
            color: 'var(--kate-type-primary)',
            background: 'var(--kate-panel)',
            fontSize: '10px',
            padding: {
              top: 4,
              bottom: 4,
              left: 8,
              right: 8,
            },
          },
          orientation: 'horizontal',
          position: 'top',
          textAnchor: isLastMonth ? 'end' : 'middle',
        },
      }];
    },
    // Current month in YYYY-MM, UTC-based, for comparison
    currentPeriod() {
      const now = new Date();
      return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
    },
    // For months in the future, recorded OTJ carries the last cumulative value
    visibleEndIndex() {
      const currentPeriodIndex = this.expectedMonthlyOtj.findIndex(p => p.period === this.currentPeriod);
      return currentPeriodIndex === -1 ? this.expectedMonthlyOtj.length : currentPeriodIndex;
    },
    lineChartSeries() {
      return this.expectedMonthlyOtj.map(e => e.cumulative_otj_minutes / 60);
    },
    mixedLineSeries() {
      const visibleSeries = this.recordedMonthlyOtj.filter(e => CHART_VISIBLE_STATUSES.includes(e.status));
      const normalisedSeries = CHART_VISIBLE_STATUSES.map(status => {
        const statusSeriesData = visibleSeries
          .filter(e => e.status === status)
          .map(e => e.cumulative_otj_minutes / 60);
        const finalVisibleValue = statusSeriesData[Math.max(this.visibleEndIndex, 0)] || 0;
        return statusSeriesData.map((value, idx) => (idx <= this.visibleEndIndex ? value : finalVisibleValue));
      });

      const [approvedSeries, pendingSeries, draftSeries] = normalisedSeries;
      const stackedPendingSeries = pendingSeries.map((value, idx) => (approvedSeries[idx] || 0) + value);
      const stackedDraftSeries = draftSeries.map((value, idx) => (approvedSeries[idx] || 0) + (pendingSeries[idx] || 0) + value);

      const statusSeries = CHART_VISIBLE_STATUSES.map((status, idx) => {
        const stackedSeriesByStatus = {
          approved: approvedSeries,
          pending: stackedPendingSeries,
          draft: stackedDraftSeries,
        };
        const zIndexByStatus = {
          approved: 3,
          pending: 2,
          draft: 1,
        };

        return ({
          name: `${status.charAt(0).toUpperCase() + status.slice(1)} hours`,
          data: stackedSeriesByStatus[status] || normalisedSeries[idx] || [],
          zIndex: zIndexByStatus[status] || 0,
        });
      });
      return [
        ...statusSeries,
        {
          name: 'Expected hours',
          data: this.lineChartSeries,
        },
      ];
    },
    mixedLineColours() {
      // Keep planned/expected line on the legacy purple used previously.
      return [...this.colours, 'var(--kate-line-warning)'];
    },
    mixedLineDashArray() {
      return [0, 0, 6, 6];
    },
    mixedLineStrokeCurve() {
      return ['smooth', 'smooth', 'smooth', 'straight'];
    },
    chartAccessibilityLabel() {
      return 'Off-the-job hours progress line chart';
    },
    chartAccessibilityDescription() {
      if (!this.dateLabels.length) {
        return 'Tracks approved, pending, draft, and expected cumulative hours over time.';
      }
      const firstPeriod = this.dateLabels[0];
      const lastPeriod = this.dateLabels[this.dateLabels.length - 1];
      let description = `Tracks approved, pending, draft, and expected cumulative hours from ${firstPeriod} to ${lastPeriod}. Draft and expected lines are dashed, with expected shown as a straight line.`;
      if (this.graduationXaxisAnnotations.length) {
        description += ` A vertical line marks the graduation date in ${this.graduationMonthEntry.pretty_period}.`;
      }
      return description;
    },
    prettyStatuses() {
      return CHART_VISIBLE_STATUSES.map(status => `${status.charAt(0).toUpperCase() + status.slice(1)} hours`);
    },
    colours() {
      return CHART_VISIBLE_STATUSES.map(status => COLOUR_MAP[status] || 'var(--kate-type-light)');
    },
    otjChartDataEndpoint() {
      if (this.isApp) {
        return `/api/curriculum/programmes/${this.programmeId}/monthly-otj-chart-data`;
      }
      return `/api/curriculum/admin/programmes/${this.programmeId}/monthly-otj-chart-data/${this.userId}`;
    },
  },
  methods: {
    getMonthlyDataAtIndex(dataPointIndex) {
      if (!this.recordedMonthlyOtj.length) return null;
      const zeroedData = { cumulative_otj_minutes: 0, monthly_otj_minutes: 0 };
      return {
        approved: this.visibleEndIndex >= dataPointIndex ? this.approvedMonthlyOtj[dataPointIndex] : zeroedData,
        pending: this.visibleEndIndex >= dataPointIndex ? this.pendingMonthlyOtj[dataPointIndex] : zeroedData,
        draft: this.visibleEndIndex >= dataPointIndex ? this.draftMonthlyOtj[dataPointIndex] : zeroedData,
      };
    },

    formatCustomTooltip({ dataPointIndex }) {
      if (!this.recordedMonthlyOtj.length) return '';

      // For future months, only show the expected hours in the tooltip
      if (dataPointIndex > this.visibleEndIndex) {
        return `<div class="custom-tooltip-content">
          <div class="tooltip-block-title">${this.dateLabels[dataPointIndex] || ''} - Expected OTJ</div>
          <div class="tooltip-section">
            <div class="tooltip-section-title">This Month:</div>
            ${this.formatTimeSpent(this.expectedMonthlyOtj[dataPointIndex]?.monthly_otj_minutes || 0)}
          </div>
          <div class="tooltip-section">
            <div class="tooltip-section-title">Total:</div>
            ${this.formatTimeSpent(this.expectedMonthlyOtj[dataPointIndex]?.cumulative_otj_minutes || 0)}
          </div>
        </div>`;
      }

      // Data for that month
      const monthlyRecordedData = this.getMonthlyDataAtIndex(dataPointIndex);
      // Recorded OTJ for each status that month
      const statusItems = CHART_VISIBLE_STATUSES.map((status, i) => {
        const minutes = monthlyRecordedData[status]?.monthly_otj_minutes || 0;
        return `<div class="tooltip-series-item">
          <span class="tooltip-marker" style="background-color: ${COLOUR_MAP[status]};"></span>
          <span class="tooltip-series-name">${this.prettyStatuses[i]}:</span>
          <span class="tooltip-value">${this.formatTimeSpent(minutes)}</span>
        </div>`;
      }).join('');

      // Approved cumulative and monthly hours vs expected
      const approvedCumulative = monthlyRecordedData.approved?.cumulative_otj_minutes || 0;
      const approvedMonthly = monthlyRecordedData.approved?.monthly_otj_minutes || 0;
      const expectedCumulative = this.expectedMonthlyOtj[dataPointIndex]?.cumulative_otj_minutes || 0;
      const expectedMonthly = this.expectedMonthlyOtj[dataPointIndex]?.monthly_otj_minutes || 0;
      return `<div class="custom-tooltip-content">
        <div class="tooltip-block-title">${this.dateLabels[dataPointIndex] || ''}</div>
        <div class="tooltip-section">${statusItems}</div>
        <div class="tooltip-section last">
          <div class="tooltip-section-title">Approved vs Expected:</div>
          ${this.formatTimeSpent(approvedMonthly)} / ${this.formatTimeSpent(expectedMonthly)}
        </div>
        <div class="tooltip-block-title">Total to Date</div>
        <div class="tooltip-section">
          <div class="tooltip-section-title">Approved vs Expected:</div>
          ${this.formatTimeSpent(approvedCumulative)} / ${this.formatTimeSpent(expectedCumulative)}
        </div>
      </div>`;
    },
    getOtjChartData() {
      this.ready = false;
      this.$logger.info('Getting monthly OTJ progress');
      this.$http.get(this.otjChartDataEndpoint).then(resp => {
        this.otjProgress = resp.data;
        this.$logger.info('Got monthly OTJ progress');
      }).catch(err => {
        this.$logger.autowarn('Could not get monthly OTJ progress', { programmeId: this.programmeId }, err);
        if (this.$http.errIn(err, [404])) {
          this.$ktoast.info('No OTJ data found for this programme');
        } else {
          this.$ktoast.showError('Could not get monthly OTJ progress', !this.isApp);
        }
      }).finally(() => {
        this.ready = true;
      });
    },
  },
};
</script>
